import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Box, Button, Chip, Divider, IconButton, List, ListItemButton, ListItemText, Popover, Tooltip, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { AppNotification } from '../../interfaces/notification.interface';
import { helpWhy } from '../../help/why';
import { formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { isSafeDeepLink, NOTIFICATIONS_CHANGED_EVENT, severityColor } from './notificationText';

export const POLL_INTERVAL_MS = 60_000;

/** Header bell: unread badge polled every 60 s, dropdown of the latest 10. Silent when nothing is selected or the call fails. */
const NotificationBell: React.FC = () => {
    const navigate = useNavigate();
    // Re-poll immediately when the selected institution or the signed-in user changes (impersonation
    // enter/exit swaps the user without a page reload).
    const { selectedInstitutionId, user } = useAuth();
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState<AppNotification[]>([]);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const mounted = useRef(true);

    const refreshCount = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            // No institution (e.g. back to a platform admin after impersonation): drop the old count.
            if (mounted.current) setUnread(0);
            return;
        }
        try {
            const count = await NotificationService.unreadCount(institutionId);
            if (mounted.current) setUnread(count);
        } catch {
            // A failed poll must never break the header; the next tick retries.
        }
    }, []);

    const loadLatest = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            const page = await NotificationService.list(institutionId, { size: 10 });
            if (mounted.current) setItems(page.content);
        } catch {
            if (mounted.current) setItems([]);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        refreshCount();
        const timer = setInterval(refreshCount, POLL_INTERVAL_MS);
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
        return () => {
            window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
            mounted.current = false;
            clearInterval(timer);
        };
    }, [refreshCount, selectedInstitutionId, user?.username]);

    const open = (e: React.MouseEvent<HTMLElement>) => {
        setAnchor(e.currentTarget);
        loadLatest();
    };

    const openItem = async (n: AppNotification) => {
        setAnchor(null);
        if (!n.readAt) {
            try {
                await NotificationService.markRead(n.id);
                setUnread(c => Math.max(0, c - 1));
            } catch {
                // Navigation still proceeds.
            }
        }
        if (isSafeDeepLink(n.deepLink)) navigate(n.deepLink);
    };

    const markAll = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            await NotificationService.markAllRead(institutionId);
            setUnread(0);
            setItems(list => list.map(n => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })));
        } catch {
            // Leave the badge as is; the next poll reconciles.
        }
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton onClick={open} data-help="notifications.bell.button" aria-label={`Notifications, ${unread} unread`} size="small" sx={{ color: 'text.secondary' }}>
                    <Badge badgeContent={unread} color="error" max={99}>
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: { xs: 'calc(100vw - 32px)', sm: 380 }, maxWidth: 420 }} data-help-page="notifications.bell.page">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                        <Typography variant="subtitle1">Notifications</Typography>
                        <Button size="small" onClick={markAll} disabled={unread === 0} data-help="notifications.bell.markAll" {...helpWhy(unread === 0 ? { kind: 'status', message: 'You have no unread notifications.' } : null)}>Mark all read</Button>
                    </Box>
                    <Divider />
                    {items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>You are all caught up.</Typography>
                    ) : (
                        <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
                            {items.map(n => (
                                <ListItemButton key={n.id} onClick={() => openItem(n)} data-help="notifications.bell.item" sx={{ alignItems: 'flex-start', bgcolor: n.readAt ? undefined : 'action.hover' }}>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span style={{ fontWeight: n.readAt ? 400 : 600 }}>{n.title}</span>
                                                {n.severity !== 'INFO' && <Chip size="small" label={n.severity} color={severityColor(n.severity)} />}
                                            </Box>
                                        }
                                        secondary={`${n.body ?? ''}${n.body ? ' · ' : ''}${formatDateTime(n.createdAt)}`}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                    <Divider />
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button size="small" onClick={() => { setAnchor(null); navigate('/notifications'); }} data-help="notifications.bell.viewAll">View all</Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default NotificationBell;
